/**
 * Las perillas del banco de pruebas.
 *
 * Cada control dice qué significa el número **en términos de conducción**, no
 * en unidades del motor. «0,5 m/s²» no le dice nada a nadie; «subir 18 km/h en
 * 10 s» sí, y es exactamente la misma condición. Un umbral que no se puede
 * explicar en una frase no se puede defender frente a un cliente.
 */

import type { TunableThresholds } from "@core/contracts/api.ts";

export type Knob = {
  key: keyof TunableThresholds;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  /** Qué exige este número, dicho en conducción y a la cadencia real. */
  explain: (value: number, intervalSeconds: number | null) => string;
};

const kmh = (mps2: number, seconds: number | null) =>
  seconds === null ? null : Math.round(Math.abs(mps2) * seconds * 3.6 * 10) / 10;

export const KNOBS: Knob[] = [
  {
    key: "lateralAccelerationMps2",
    label: "Giro por aceleración lateral desde",
    unit: "m/s²",
    min: 0,
    max: 8,
    step: 0.1,
    explain: (value) => {
      if (value <= 0) {
        return (
          "Apagado: manda la velocidad angular fija de abajo. Es el criterio " +
          "del Pipeline V5 original y se equivoca en las dos direcciones — a " +
          "baja velocidad cuenta curvas de calle, y en carretera deja pasar " +
          "giros que sí son fuertes."
        );
      }
      // Se dice qué exige a dos velocidades reales, porque el mismo número
      // pide cosas distintas según a qué velocidad se vaya — que es
      // exactamente la razón de usar aceleración lateral y no °/s.
      const gradosPorSegundo = (kmhVel: number) =>
        Math.round(((value / (kmhVel / 3.6)) * 57.2958) * 10) / 10;
      return (
        `Cuenta como giro el que empuje al pasajero con ${value} m/s². ` +
        `A 40 km/h eso son ${gradosPorSegundo(40)} °/s; a 80 km/h, ` +
        `${gradosPorSegundo(80)} °/s. El manual de GuardIA usa 3,4.`
      );
    },
  },
  {
    key: "sustainedWindowSeconds",
    label: "Velocidad sostenida durante",
    unit: "s",
    min: 0,
    max: 60,
    step: 1,
    explain: (value, seconds) => {
      if (value <= 0) {
        return (
          "Apagado: exige que UN SOLO tramo dure los segundos mínimos. Con un " +
          "equipo que reporte más seguido que eso, ningún tramo llega nunca y " +
          "el detector queda ciego — cuanto mejor el dato, menos ve."
        );
      }
      const tramos = seconds ? Math.max(1, Math.ceil(value / seconds)) : null;
      return (
        `Cuenta como exceso ir por encima del límite durante ${value} s seguidos, ` +
        (tramos === null
          ? "sumando los tramos que hagan falta."
          : `lo que a ${seconds} s entre reportes son ${tramos} tramo(s) encadenados.`) +
        " La racha entera es un episodio, no uno por tramo."
      );
    },
  },
  {
    key: "accelerationThresholdMps2",
    label: "Aceleración brusca desde",
    unit: "m/s²",
    min: 0.1,
    max: 4,
    step: 0.05,
    explain: (value, seconds) => {
      const delta = kmh(value, seconds);
      return delta === null
        ? "Aceleración media entre dos tramos."
        : `Marca como brusca una aceleración que suba ${delta} km/h en los ${seconds} s que hay entre dos reportes.`;
    },
  },
  {
    key: "minimumSpeedChangeKmh",
    label: "Cambio mínimo de velocidad",
    unit: "km/h",
    min: 0,
    max: 30,
    step: 0.5,
    explain: (value) =>
      `Por debajo de ${value} km/h de cambio no cuenta, aunque la aceleración dé alto. Filtra el ruido de velocidad a baja marcha.`,
  },
  {
    key: "brakingThresholdMps2",
    label: "Frenada brusca desde",
    unit: "m/s²",
    min: -6,
    max: -0.1,
    step: 0.05,
    explain: (value, seconds) => {
      const delta = kmh(value, seconds);
      return delta === null
        ? "Desaceleración media entre dos tramos."
        : `Marca como brusca una frenada que baje ${delta} km/h en los ${seconds} s entre dos reportes.`;
    },
  },
  {
    key: "minimumYawRateDegreesPerSecond",
    label: "Giro brusco desde",
    unit: "°/s",
    min: 0.1,
    max: 30,
    step: 0.1,
    explain: (value, seconds) =>
      seconds === null
        ? "Velocidad de giro."
        : `Marca como brusco un giro que cambie ${Math.round(value * seconds * 10) / 10}° en los ${seconds} s entre dos reportes.`,
  },
  {
    key: "minimumHeadingChangeDegrees",
    label: "Cambio de rumbo mínimo",
    unit: "°",
    min: 1,
    max: 120,
    step: 1,
    explain: (value) =>
      `Un cambio de rumbo menor a ${value}° no se considera giro: a esa escala el rumbo del GPS es ruido, no maniobra.`,
  },
  {
    key: "minimumReferenceSpeedKmh",
    label: "Velocidad mínima para contar un giro",
    unit: "km/h",
    min: 0,
    max: 80,
    step: 1,
    explain: (value) =>
      `Girar a menos de ${value} km/h no cuenta. Maniobrar en un parqueadero no es un giro de riesgo.`,
  },
  {
    key: "operationalSpeedLimitKmh",
    label: "Velocidad elevada desde",
    unit: "km/h",
    min: 40,
    max: 200,
    step: 1,
    explain: (value) =>
      `Tramos sostenidos por encima de ${value} km/h. Es un umbral operacional, no un límite legal de vía: no implica infracción.`,
  },
  {
    key: "operationalSpeedExitKmh",
    label: "La racha no se corta hasta bajar de",
    unit: "km/h",
    min: 0,
    max: 200,
    step: 1,
    explain: (value) => {
      if (value <= 0) {
        return (
          "Apagado: la racha se corta en cuanto la velocidad deja de superar " +
          "el límite de arriba, así que cada cruce abre un episodio nuevo. " +
          "Medido en la flota con el límite en 80 km/h, un equipo de 10 s dio " +
          "839 tramos por encima en 3.104 km de autopista a 80–86 km/h: el " +
          "mismo trecho contado cientos de veces, y esa cuenta es la que " +
          "alimenta la tasa por 100 km."
        );
      }
      return (
        `Una vez que la velocidad supera el límite de arriba, el exceso sigue ` +
        `siendo el MISMO episodio hasta que se baje de ${value} km/h. Oscilar ` +
        `alrededor del límite deja de contar un solo trecho varias veces. ` +
        `Un valor por encima del límite de entrada no describe ninguna ` +
        `histéresis y el motor lo trata como apagado; y sin la ventana de ` +
        `velocidad sostenida encendida, esta perilla no hace nada, porque no ` +
        `hay racha que mantener abierta.`
      );
    },
  },
];

export function ThresholdControls({
  thresholds,
  intervalSeconds,
  onChange,
}: {
  thresholds: TunableThresholds;
  intervalSeconds: number | null;
  onChange: (key: keyof TunableThresholds, value: number) => void;
}) {
  return (
    <div className="space-y-4">
      {KNOBS.map((knob) => {
        const value = thresholds[knob.key] as number;
        return (
          <div key={knob.key}>
            <div className="flex items-baseline justify-between gap-2">
              <label htmlFor={knob.key} className="text-xs font-medium text-zinc-700 dark:text-zinc-300">
                {knob.label}
              </label>
              <div className="flex items-center gap-1">
                <input
                  id={knob.key}
                  type="number"
                  className="w-20 rounded border border-zinc-300 bg-white px-1.5 py-0.5 text-right text-sm tabular-nums text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                  value={value}
                  min={knob.min}
                  max={knob.max}
                  step={knob.step}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (Number.isFinite(next)) onChange(knob.key, next);
                  }}
                />
                <span className="w-10 text-xs text-zinc-500">{knob.unit}</span>
              </div>
            </div>
            <input
              type="range"
              aria-label={knob.label}
              className="mt-1 w-full accent-brand-600"
              value={value}
              min={knob.min}
              max={knob.max}
              step={knob.step}
              onChange={(event) => onChange(knob.key, Number(event.target.value))}
            />
            <p className="mt-0.5 text-xs leading-snug text-zinc-500 dark:text-zinc-400">
              {knob.explain(value, intervalSeconds)}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/** Bandas de severidad: tres cortes crecientes por familia. */
export function SeverityControls({
  thresholds,
  onChange,
}: {
  thresholds: TunableThresholds;
  onChange: (key: keyof TunableThresholds, value: [number, number, number]) => void;
}) {
  const rows: { key: keyof TunableThresholds; label: string; unit: string; step: number }[] = [
    { key: "severityAcceleration", label: "Aceleración", unit: "m/s²", step: 0.05 },
    { key: "severityBraking", label: "Frenada", unit: "m/s² (absoluto)", step: 0.05 },
    { key: "severityYawRate", label: "Giro", unit: "°/s", step: 0.1 },
  ];

  return (
    <div className="space-y-3">
      <p className="text-xs leading-snug text-zinc-500 dark:text-zinc-400">
        Un episodio detectado se clasifica por su magnitud. Los tres cortes tienen que ir de menor a
        mayor, o habría episodios «críticos» más suaves que otros «altos».
      </p>
      {rows.map((row) => {
        const band = thresholds[row.key] as [number, number, number];
        return (
          <div key={row.key} className="flex flex-wrap items-center gap-2">
            <span className="w-24 text-xs font-medium text-zinc-700 dark:text-zinc-300">{row.label}</span>
            {(["Moderada", "Alta", "Crítica"] as const).map((label, index) => (
              <label key={label} className="flex items-center gap-1 text-xs text-zinc-500">
                {label}
                <input
                  type="number"
                  className="w-16 rounded border border-zinc-300 bg-white px-1 py-0.5 text-right text-sm tabular-nums text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                  value={band[index]}
                  step={row.step}
                  onChange={(event) => {
                    const next = [...band] as [number, number, number];
                    next[index] = Number(event.target.value);
                    onChange(row.key, next);
                  }}
                />
              </label>
            ))}
            <span className="text-xs text-zinc-400">{row.unit}</span>
          </div>
        );
      })}
    </div>
  );
}
